import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuMultiSelectGroupProps,
	type MenuRootProps,
	type MenuVariantProps,
	Text,
} from "@overmap-ai/blocks"
import { type Asset, pluralize, type Stored, UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement, useDeferredValue } from "react"

import { assetFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { assetSortFn } from "../../sorting/utils"
import { AssetTypeIcon } from "../Icons/AssetTypeIcon"
import { AssetIndexLabel } from "../IndexLabels/AssetIndexLabel"
import { MultiSelectItemTemplate, SelectAllItemTemplate } from "../MenuGroups/constants"

interface AssetMultiSelectProps
	extends
		MenuRootProps,
		MenuMultiSelectGroupProps<Stored<Asset>>,
		MenuVariantProps,
		Omit<MenuClickTriggerProps, "children" | "ref"> {
	children: ReactElement
	assets: Stored<Asset>[]
}

export const AssetMultiSelect = memo((props: AssetMultiSelectProps) => {
	const { children, assets, disabled, variant, size, accentColor, ...rest } = props

	const sortedAssets = useSort(assets, assetSortFn)

	const [filteredAssets, filterValue, setFilterValue] = useTextFilter<Stored<Asset>>(
		sortedAssets,
		assetFilterCallback,
	)

	const deferredFilteredAssets = useDeferredValue(filteredAssets)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{deferredFilteredAssets.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					<Menu.MultiSelectGroup<Stored<Asset>> allValues={deferredFilteredAssets} {...rest}>
						{deferredFilteredAssets.length > 0 && (
							<SelectAllItemTemplate>
								<div className="flex grow items-center justify-between gap-2">
									Select all
									<Text className="truncate" accentColor="base">
										{deferredFilteredAssets.length}{" "}
										{pluralize("asset", deferredFilteredAssets.length)}
									</Text>
								</div>
							</SelectAllItemTemplate>
						)}
						{deferredFilteredAssets.slice(0, 30).map((asset) => (
							<MultiSelectItemTemplate<Stored<Asset>> key={asset.uuid} value={asset}>
								<AssetTypeIcon assetTypeId={asset.asset_type} />
								<AssetIndexLabel assetId={asset.uuid} />
								<Text className="truncate" accentColor="base">
									{asset.label || UNLABELLED_ASSET_LABEL}
								</Text>
							</MultiSelectItemTemplate>
						))}
					</Menu.MultiSelectGroup>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetMultiSelect.displayName = "AssetMultiSelect"
